import type { UseMutationOptions } from '@tanstack/react-query';

import type { KibanaLogger } from '@revelio/iso-utility';

import { DataBuilderHttpError } from './errors';

// Status codes the Data Builder backend uses to surface expected user-input
// validation feedback. Errors with these statuses are rendered inline by the
// caller and should not be logged to Kibana. Add new codes here as more
// endpoints adopt the pattern.
const EXPECTED_VALIDATION_STATUSES = new Set<number>([
  422, // Unprocessable Entity
]);

const isExpectedValidationError = (error: Error): boolean =>
  error instanceof DataBuilderHttpError &&
  EXPECTED_VALIDATION_STATUSES.has(error.status);

/**
 * Decorates `useMutation` options with the Data Builder mutation policy:
 * opts the mutation out of the global error handler, silences expected
 * validation (422) errors, and logs everything else to Kibana with the
 * originating request id. Spread the result into `useMutation` so the policy
 * follows the mutation wherever it's used.
 */
export const withDataBuilderMutationPolicy = <
  TData,
  TVariables,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, Error, TVariables, TContext>,
  kibanaLogger: KibanaLogger | undefined
): UseMutationOptions<TData, Error, TVariables, TContext> => ({
  ...options,
  meta: { ...options.meta, skipGlobalErrorHandling: true },
  onError: (error, variables, context) => {
    const getRequestId = (error: Error): string | undefined =>
      error instanceof DataBuilderHttpError ? error.requestId : undefined;
    if (!isExpectedValidationError(error)) {
      kibanaLogger?.error(`Data Builder mutation failed: ${error.message}`, {
        'request-id': getRequestId(error),
        url: window.location.href,
      });
    }

    options.onError?.(error, variables, context);
  },
});
